import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bike, Crosshair, Lock, Store } from 'lucide-react';
import { computeTotals, distanceMeters, pseudoGeocode, tipFromPercent } from '@pizza-roma/shared';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { rememberOrder, useCart } from '../../lib/cart';
import { money } from '../../lib/format';
import { MapView } from '../../lib/map';
import { useI18n } from '../../i18n';
import { usePublic } from './context';
import { useLineOptions } from './MenuPage';

const TIPS = [0, 10, 15, 20];

export function CheckoutPage() {
  const { restaurant: r } = usePublic();
  const { t, demo } = useI18n();
  const lineOptions = useLineOptions();
  const cart = useCart();
  const { me } = useAuth();
  const nav = useNavigate();
  const [address, setAddress] = useState({ line1: '', line2: '', city: r.address.city ?? 'Montréal', postalCode: '', instructions: '' });
  const [pin, setPin] = useState<{ lat: number; lng: number } | null>(null);
  const [contact, setContact] = useState({ name: me?.user?.displayName ?? '', phone: '', email: me?.user?.email ?? '' });
  const [instructions, setInstructions] = useState('');
  const [tipPercent, setTipPercent] = useState(15);
  const [card, setCard] = useState({ number: '4242 4242 4242 4242', expiry: '12/30', cvc: '123', name: '' });
  const [pending, setPending] = useState<{ publicToken: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const delivery = cart.mode === 'DELIVERY';
  const fee = delivery ? r.deliveryFeeCents : 0;
  const tip = delivery ? tipFromPercent(cart.subtotalCents, tipPercent) : 0;
  const totals = computeTotals({ subtotalCents: cart.subtotalCents, deliveryFeeCents: fee, tipCents: tip, taxRatePercent: r.taxRatePercent });

  const approx = useMemo(
    () => (address.line1.length >= 3 ? pseudoGeocode(r.location, `${address.line1} ${address.city} ${address.postalCode}`) : null),
    [address.line1, address.city, address.postalCode, r.location],
  );
  const dest = pin ?? approx;
  const km = dest ? distanceMeters(r.location, dest) / 1000 : 0;
  const outOfZone = delivery && dest != null && km > r.deliveryRadiusKm;

  if (cart.lines.length === 0 && !pending)
    return (
      <main className="pub-main">
        <div className="card card-pad center stack">
          <p>{t.cart.empty}</p>
          <Link className="btn btn-primary" to={`/r/${r.slug}/menu`}>
            {t.common.viewMenu}
          </Link>
        </div>
      </main>
    );

  const useMyLocation = () => {
    navigator.geolocation?.getCurrentPosition(
      (p) => setPin({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => setError(t.checkout.locationDenied),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (outOfZone) return setError(t.checkout.outOfZone(km.toFixed(1), r.deliveryRadiusKm));
    setBusy(true);
    try {
      let token = pending?.publicToken;
      if (!token) {
        const created = await api.post<{ publicToken: string }>(`/api/restaurants/${r.slug}/orders`, {
          fulfillmentType: cart.mode,
          items: cart.lines.map((l) => ({ menuItemId: l.menuItemId, quantity: l.quantity, selections: l.selections })),
          customer: contact,
          address: delivery ? { ...address, ...(pin ?? {}) } : undefined,
          instructions,
          tipPercent: delivery ? tipPercent : 0,
        });
        token = created.publicToken;
        setPending({ publicToken: token });
      }
      await api.post(`/api/public/orders/${token}/pay`, { card: { number: card.number, expiry: card.expiry, cvc: card.cvc, name: card.name } });
      rememberOrder(r.slug, { publicToken: token, createdAt: new Date().toISOString() });
      cart.clear();
      nav(`/r/${r.slug}/order/${token}`, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t.checkout.networkError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="pub-main">
      <div className="steps">
        <span className="step on">
          <b>1</b> {delivery ? t.common.delivery : t.common.pickup}
        </span>
        <span className="muted">→</span>
        <span className="step on">
          <b>2</b> {t.checkout.stepContact}
        </span>
        <span className="muted">→</span>
        <span className="step on">
          <b>3</b> {t.checkout.stepPayment}
        </span>
      </div>
      <form className="checkout" onSubmit={submit}>
        <div className="stack-lg">
          <section className="card card-pad stack">
            <h3>{t.checkout.receptionMode}</h3>
            <div className="mode-cards">
              <button type="button" className={`mode-card ${delivery ? 'active' : ''}`} onClick={() => cart.setMode('DELIVERY')} disabled={!r.deliveryEnabled || !!pending} data-testid="mode-delivery">
                <Bike color="var(--red)" />
                <span>
                  <b>{t.common.delivery}</b>
                  <br />
                  <small className="muted">{t.checkout.deliveryHint}</small>
                </span>
              </button>
              <button type="button" className={`mode-card ${!delivery ? 'active' : ''}`} onClick={() => cart.setMode('PICKUP')} disabled={!r.pickupEnabled || !!pending} data-testid="mode-pickup">
                <Store color="var(--red)" />
                <span>
                  <b>{t.common.pickup}</b>
                  <br />
                  <small className="muted">{t.checkout.pickupHint}</small>
                </span>
              </button>
            </div>
          </section>

          {delivery ? (
            <section className="card card-pad stack">
              <h3>{t.checkout.deliveryAddress}</h3>
              <label className="field">
                <span>{t.common.address}</span>
                <input className="input" required minLength={3} value={address.line1} onChange={(e) => setAddress({ ...address, line1: e.target.value })} placeholder={t.checkout.addressPlaceholder} autoComplete="street-address" data-testid="address-line1" disabled={!!pending} />
              </label>
              <div className="form-row">
                <label className="field">
                  <span>{t.checkout.apartment}</span>
                  <input className="input" value={address.line2} onChange={(e) => setAddress({ ...address, line2: e.target.value })} disabled={!!pending} />
                </label>
                <label className="field">
                  <span>{t.common.city}</span>
                  <input className="input" required value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} disabled={!!pending} />
                </label>
              </div>
              <div className="form-row">
                <label className="field">
                  <span>{t.common.postalCode}</span>
                  <input className="input" value={address.postalCode} onChange={(e) => setAddress({ ...address, postalCode: e.target.value })} autoComplete="postal-code" disabled={!!pending} />
                </label>
                <label className="field">
                  <span>{t.checkout.instructions}</span>
                  <input className="input" value={address.instructions} onChange={(e) => setAddress({ ...address, instructions: e.target.value })} placeholder={t.checkout.instructionsPlaceholder} disabled={!!pending} />
                </label>
              </div>
              <div className="row-between wrap">
                <span className="small muted">
                  {pin ? t.checkout.pinPlaced : t.checkout.tapMap} {dest && t.checkout.distance(km.toFixed(1))}
                </span>
                <button type="button" className="btn btn-sm" onClick={useMyLocation} disabled={!!pending}>
                  <Crosshair size={16} /> {t.checkout.myLocation}
                </button>
              </div>
              <div style={{ height: 240 }}>
                <MapView
                  center={r.location}
                  zoom={12}
                  markers={[{ id: 'r', kind: 'restaurant', ...r.location }, ...(dest ? [{ id: 'h', kind: 'home' as const, ...dest }] : [])]}
                  fitKey={dest ? `${dest.lat.toFixed(4)},${dest.lng.toFixed(4)}` : 'none'}
                  onMapClick={pending ? undefined : setPin}
                />
              </div>
              {outOfZone && <div className="alert alert-error">{t.checkout.outOfZoneAlert(r.deliveryRadiusKm)}</div>}
            </section>
          ) : (
            <section className="card card-pad stack">
              <h3>{t.checkout.pickupAtRestaurant}</h3>
              <p>
                {r.address.line1}, {r.address.city}
              </p>
              <p className="muted small">{t.checkout.readyIn(r.prepTimeMinutes)}</p>
            </section>
          )}

          <section className="card card-pad stack">
            <h3>{t.checkout.stepContact}</h3>
            <label className="field">
              <span>{t.checkout.fullName}</span>
              <input className="input" required value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} autoComplete="name" data-testid="contact-name" disabled={!!pending} />
            </label>
            <div className="form-row">
              <label className="field">
                <span>{t.common.phone}</span>
                <input className="input" required minLength={7} type="tel" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} autoComplete="tel" data-testid="contact-phone" disabled={!!pending} />
              </label>
              <label className="field">
                <span>{t.common.email}</span>
                <input className="input" required type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} autoComplete="email" data-testid="contact-email" disabled={!!pending} />
              </label>
            </div>
            <label className="field">
              <span>{t.checkout.kitchenNote}</span>
              <textarea className="textarea" value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder={t.checkout.kitchenNotePlaceholder} disabled={!!pending} />
            </label>
          </section>
        </div>

        <aside className="stack-lg" style={{ position: 'sticky', top: 90 }}>
          <section className="card card-pad stack">
            <div className="row-between">
              <h3>{t.menu.yourOrder}</h3>
              <Link to={`/r/${r.slug}/cart`} className="small" style={{ color: 'var(--red)' }}>
                {t.checkout.edit}
              </Link>
            </div>
            {cart.lines.map((l) => {
              const options = lineOptions(l);
              return (
                <div key={l.key} className="row-between small">
                  <span>
                    {l.quantity} × {demo(l.name)}
                    {options && <span className="muted"> ({options})</span>}
                  </span>
                  <b>{money(l.unitPriceCents * l.quantity)}</b>
                </div>
              );
            })}
            {delivery && (
              <>
                <h4 style={{ marginTop: 8 }}>{t.checkout.tipTitle}</h4>
                <div className="tips">
                  {TIPS.map((p) => (
                    <button type="button" key={p} className={tipPercent === p ? 'active' : ''} onClick={() => setTipPercent(p)} disabled={!!pending}>
                      {p === 0 ? t.checkout.noTip : t.common.percent(p)}
                      {p > 0 && <small>{money(tipFromPercent(cart.subtotalCents, p))}</small>}
                    </button>
                  ))}
                </div>
              </>
            )}
            <div className="totals">
              <div className="row-between">
                <span className="muted">{t.common.subtotal}</span>
                <span>{money(totals.subtotalCents)}</span>
              </div>
              {delivery && (
                <div className="row-between">
                  <span className="muted">{t.common.delivery}</span>
                  <span>{money(totals.deliveryFeeCents)}</span>
                </div>
              )}
              <div className="row-between">
                <span className="muted">{t.common.taxes}</span>
                <span>{money(totals.taxCents)}</span>
              </div>
              {delivery && (
                <div className="row-between">
                  <span className="muted">{t.common.tip}</span>
                  <span>{money(totals.tipCents)}</span>
                </div>
              )}
              <div className="row-between total">
                <span>{t.common.total}</span>
                <span data-testid="checkout-total">{money(totals.totalCents)}</span>
              </div>
            </div>
          </section>

          <section className="card card-pad stack">
            <div className="row-between">
              <h3>{t.common.payment}</h3>
              <span className="badge no-dot b-demo">{t.common.demoBadge}</span>
            </div>
            <p className="small muted">{t.checkout.demoNotice}</p>
            <label className="field">
              <span>{t.checkout.cardNumber}</span>
              <input className="input money" inputMode="numeric" required value={card.number} onChange={(e) => setCard({ ...card, number: e.target.value })} data-testid="card-number" />
            </label>
            <div className="form-row">
              <label className="field">
                <span>{t.checkout.expiry}</span>
                <input className="input" required pattern="(0[1-9]|1[0-2])/\d{2}" value={card.expiry} onChange={(e) => setCard({ ...card, expiry: e.target.value })} />
              </label>
              <label className="field">
                <span>CVC</span>
                <input className="input" required pattern="\d{3,4}" inputMode="numeric" value={card.cvc} onChange={(e) => setCard({ ...card, cvc: e.target.value })} />
              </label>
            </div>
            {error && (
              <div className="alert alert-error" data-testid="checkout-error">
                {error}
              </div>
            )}
            <button className="btn btn-green btn-lg btn-block" disabled={busy || outOfZone} data-testid="pay-button">
              <Lock size={18} /> {busy ? t.checkout.paying : t.checkout.payNow(money(totals.totalCents))}
            </button>
            <p className="small muted center">{t.checkout.stripeReady}</p>
          </section>
        </aside>
      </form>
    </main>
  );
}
